import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, HubUtil, Subtask, Tag, User} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {showMoreDeleteTaskKey} from '@client/storage'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {Tracking, TrackingEvent} from '@common/tracking'
import {showDeleteConfirm} from '@components/common/base/notifications'
import {MessageBox} from '@components/message-box'
import {TaskItem, taskItemFromHub} from '@components/my-tasks/task-item'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {GanttDraft} from '@extensions/gantt-draft'
import {isMirroredHub} from '@extensions/mirrored-tasks/is-mirrored-hub'
import {Sticker} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'

interface State {
  taskId: DataObjectId
  parentId?: DataObjectId
  location: string[]
  assignedUsers: DataObjectId[]
  pinnedStickers: DataObjectId[]
  usersStickerId?: DataObjectId
  author?: User
  title?: string
  isDone?: boolean
  isPrivate?: boolean
  deleted?: boolean
  starred?: boolean
  subtasks?: Subtask[]
}

export class TaskStore {
  state: State

  constructor(taskId: DataObjectId, parentId?: DataObjectId) {
    this.state = {
      taskId: taskId,
      parentId: parentId,
      location: [],
      assignedUsers: [],
      pinnedStickers: [],
      subtasks: []
    }

    this.syncTaskData()

    makeAutoObservable(this, {}, {autoBind: true})
  }

  syncTaskId(taskId: DataObjectId): void {
    this.state.taskId = taskId
  }

  syncTaskData(): void {
    const task = this.taskHub

    if (task) {
      this.state.isDone = Task.isDone(task)
      this.state.isPrivate = !!task?.data?.private
      this.state.deleted = task.deleted
      this.state.title = task.title
      this.state.location = ChatUtils.getSplitChatLocation(task)
      this.state.assignedUsers = Sticker.getUsers(task)
      this.state.author = Task.creator(task)

      this.state.starred = UserSettings.isStarred(task)
      this.state.subtasks = SubtaskUtil.getSubtasksFromList(
        SubtaskUtil.getSubtaskList(task)
      )
      this.state.usersStickerId = Sticker.getUserSticker(task).id
      const stickers = Sticker.getAllPinned(task) || []

      this.state.pinnedStickers =
        stickers
          ?.filter(sticker => {
            return Sticker.isCustom(sticker)
          })
          ?.map(st => st.id) || []
    }

    if (this.state.parentId) {
      const parentTask = Task.getById(this.state.parentId)
      this.state.title =
        (parentTask &&
          SubtaskUtil.getSubtaskList(parentTask).subtasks[this.state.taskId]
            ?.title) ||
        this.state.title
    }
  }

  get taskHub(): Hub | undefined {
    return Task.getById(this.state.taskId)
  }

  get canComplete(): boolean {
    const task = this.taskHub

    if (!task) {
      return false
    }

    return (
      PermissionsChecker.checkComplete(task) &&
      (!PermissionsChecker.shouldFallbackToOldCheckComplete(task) ||
        PermissionsChecker.oldCheckComplete(task))
    )
  }

  get usersSticker(): Tag | undefined {
    const box = Sync.getCurrentSandbox()

    return this.state.usersStickerId
      ? Sticker.getById(box, this.state.usersStickerId)
      : undefined
  }

  get doneCount(): number {
    return this.state.subtasks?.filter(x => x.isCompleted).length || 0
  }

  get taskItem(): TaskItem | undefined {
    const taskHub = this.taskHub

    if (taskHub) {
      const box = Sync.getCurrentSandbox()

      return taskItemFromHub(box, taskHub)
    }

    return undefined
  }

  get taskStickers(): Tag[] {
    const box = Sync.getCurrentSandbox()
    return this.state.pinnedStickers.map(stickerId => {
      return Sticker.getById(box, stickerId)
    })
  }

  get chatLocationParts(): string[] {
    const hub = this.taskHub
    return hub ? ChatUtils.getSplitChatLocation(hub) : []
  }

  public toggleComplete(): void {
    const task = this.taskHub
    if (!task) {
      return
    }

    const isDone = Task.isDone(task)

    if (!PermissionsChecker.checkComplete(task)) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }
    if (
      PermissionsChecker.shouldFallbackToOldCheckComplete(task) &&
      !PermissionsChecker.oldCheckComplete(task)
    ) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }

    Task.changeCompleted(task, !isDone, 'my-tasks')
  }

  public async deleteTask(fromWhere = 'my-tasks'): Promise<void> {
    const hub = this.taskHub
    if (!hub) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const mirror = isMirroredHub(hub)
    const text = mirror
      ? Lang.msg.wantToDeleteAMirror
      : Lang.msg.wantToDeleteATask

    if (await showDeleteConfirm(text, showMoreDeleteTaskKey)) {
      if (fromWhere === 'timeline') {
        void Tracking.send(TrackingEvent.ganttDeleteTaskViaGanttChart)
      }

      box.remove(hub)
      GanttDraft.planEntitySubtree(hub.id, box)
      if (HubUtil.isPhantom(hub)) {
        taskPhantomObserver.update()
      }
    }
  }

  public async toggleArchive(): Promise<void> {
    const hub = this.taskHub
    if (!hub) {
      return
    }
    const box = Sync.getCurrentSandbox()

    const isArchived = Task.isArchived(hub)

    await Task.changeArchived(
      {
        id: this.state.taskId,
        companyId: box.companyId
      },
      !isArchived
    )
  }

  makeTaskFavorite(): void {
    const taskItem = this.taskItem

    if (!taskItem) {
      return
    }

    const nextValue = !this.state.starred

    /*
    We do not send tracking events here, because it is already sent in UserSettings.setStarred
    if (value) {
      Tracking.send(TrackingEvent.myTasksTaskChatStarredOn)
    } else {
      Tracking.send(TrackingEvent.myTasksTaskChatStarredOff)
    }
    */

    UserSettings.setStarred(taskItem, nextValue, 'my-tasks')
  }
}

export const [TaskStoreContext, useTaskStore] = createUseStore(TaskStore)
